interface Props {
  score: number;
  outOf: number;
  status: string;
  asOfLong: string;
}

export function ScoreCard({ score, outOf, status, asOfLong }: Props) {
  return (
    <div className="border border-[var(--gold)]/40 rounded-lg bg-[#0f1623] p-6 mb-6 flex items-center gap-8">
      <div className="flex-shrink-0">
        <div className="flex items-baseline gap-1">
          <span className="text-[64px] font-bold text-[var(--gold)] leading-none">
            {score}
          </span>
          <span className="text-[24px] text-[#64748b] font-light">
            /{outOf}
          </span>
        </div>
        <div className="text-[12px] uppercase tracking-wider text-[#94a3b8] mt-1">
          {status}
        </div>
        <div className="text-[10px] text-[#64748b] mt-0.5">
          as of {asOfLong}
        </div>
      </div>
      <div className="flex-1 text-[13px] text-[#cbd5e1] leading-relaxed space-y-3">
        <p>
          Triumph&rsquo;s quantitative model was built with AI, mathematics,
          and thoughtful design. It does not compete with the largest quant
          shops &mdash; Renaissance, Two Sigma, Citadel &mdash; those operate
          at a fundamentally different infrastructure tier with thousands of
          researchers, real-time microstructure, multi-asset coverage, and
          decades of out-of-sample live track record. What this model is:
          uncommon work for a single-team manager. The level of rigor &mdash;
          transparent methodology, empirically tuned production parameters,
          walk-forward validation across twelve vintages, a deterministic
          state machine logging every decision &mdash; is well above what
          most non-quant money managers ship.
        </p>
        <p>
          <strong className="text-white">The math.</strong> The composite formula
          is an asymmetric Euclidean reward: quality and valuation combine
          geometrically when both are favourable (the cheap-quality corner
          where alpha lives) and linearly when valuation is unfavourable
          (the regime exit). Current production parameters &mdash; quality
          scale 1.25, valuation beta 0.90, and a shifted penalty threshold
          (V = &minus;0.5) so the model treats a modest premium as fair,
          not expensive
          &mdash; were chosen empirically through a leave-one-out
          walk-forward across 972 parameter combinations and twelve annual
          vintages of monthly Bloomberg fundamentals. Production survives
          four formal robustness tests: beta sensitivity, survivorship
          correction, forward-factor mix, and pillar-weight optimisation.
          The peer-group structure separates banks, REITs, insurers,
          energy, utilities, and standard nonfinancials &mdash; each scored
          on the metrics that describe its business model, not a
          one-size-fits-all price-to-earnings. Per-position asymmetry math
          anchors on each name&rsquo;s own five-year P/E history, with a
          stress floor and an upside cap, so downside and upside numbers
          are defensible rather than statistical artefacts.
        </p>
        <p>
          <strong className="text-white">The thinking.</strong> The
          conceptual core is the recognition that quality and valuation are
          not symmetric signals. Quality is what companies do &mdash; invest
          in moats, hire talent, allocate capital, build operational
          discipline. Valuation is what the market thinks. Companies control
          Q via management; no company controls its V. That asymmetry of
          agency is encoded directly in the math: Q&rsquo;s contribution is
          scaled up 25% relative to V&rsquo;s, with an inflection that
          allows a half-sigma premium for quality before the linear penalty
          branch engages. The model favours what compounds &mdash;
          slow-moving fundamentals &mdash; over what oscillates &mdash;
          multiples and sentiment. The hedge signal is a separate
          diagnostic flag marking stretched names; rather than blocking
          those names from the basket, the model surfaces them to the
          Hedge Lab so the operator can overlay structured protection. The
          basket itself is conviction-weighted (weight &prop; composite&#8309;,
          capped at 15% per name) drawn from the top 10 by composite, with
          sector and commodity-bucket caps so a regime tilt does not become
          an undiversified bet.
        </p>
        <p>
          <strong className="text-white">The system.</strong> The
          infrastructure is self-owned. Bloomberg SAPI runs on hardware
          Triumph maintains, with user-mode authentication unlocking the
          same field coverage as the terminal itself. Every weekday morning
          before six, 1,099 stocks are repriced and rescored, the Alpha
          Zone basket rebalances monthly (cadence chosen by the same
          walk-forward that picked the composite parameters), daily
          snapshots archive to a permanent record, and a deterministic
          state machine logs the reason behind every entry, exit, and
          rotation &mdash; nothing relies on a black box. A live Hedge Lab
          prices six structured strategies for every holding off real
          Bloomberg option chains with Canadian tax framework attached. A
          Research Desk surfaces per-position one-pagers explaining in
          plain English why each name scores how it does. Code is at the
          institutional baseline: zero type errors, zero lint warnings, 671
          passing tests. The points held back from 100 are partly time
          (live track is short, AUM stress is light) and partly scope (a
          single-market universe, no multi-asset, no real-time
          microstructure) &mdash; honest limits, kept deliberately so a
          single team can maintain it.
        </p>
      </div>
    </div>
  );
}
